import { Dispatch, SetStateAction, useContext } from 'react'

import { CustomerOrderBy, CustomerSearchCondition } from '@/common/api/modules/customer'
import { CustomerSummary } from '@/common/api/params/customer'
import {
  ListFollowTableHeaderProps,
  makeRectPropsForTableHeadItem,
  useListScrollFollow
} from '@/common/app/list'
import { useSiteId } from '@/common/app/site'

import ListHeaderSortButton from '@/components/view/atoms/icon/ListHeaderSortButton'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import { AccessCustomerModalContext } from '@/components/container/modal/accessCustomer/AccessCustomerModalProvider'
import AccessCustomerListRow from '@/components/container/modules/recommend/customer/table/AccessCustomerListRow'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

interface TheadProps extends ListFollowTableHeaderProps {
  orderBy: CustomerOrderBy | null
  onChangeOrderBy(orderBy: CustomerOrderBy | null): void
  float?: boolean
}

const Thead = ({
  orderBy,
  onChangeOrderBy,
  float = false,
  isScrolled,
  rowRefs,
  rowRects
}: TheadProps) => {
  const rectProps = (index: number) =>
    makeRectPropsForTableHeadItem(rowRefs, rowRects, index, float)

  return (
    <>
      <TableHeadItem checkbox float={float} {...rectProps(0)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.VISITOR_ID_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.VISITOR_ID_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.VISITOR_ID_DESC : CustomerOrderBy.VISITOR_ID_ASC
            )
          }
          text='クッキーID'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(1)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.CUSTOMER_ID_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.CUSTOMER_ID_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.CUSTOMER_ID_DESC : CustomerOrderBy.CUSTOMER_ID_ASC
            )
          }
          text='顧客ID[分析]'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(2)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.DELIVERY_COUNT_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.DELIVERY_COUNT_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc'
                ? CustomerOrderBy.DELIVERY_COUNT_DESC
                : CustomerOrderBy.DELIVERY_COUNT_ASC
            )
          }
          text='配信数'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(3)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.VIEW_COUNT_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.VIEW_COUNT_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.VIEW_COUNT_DESC : CustomerOrderBy.VIEW_COUNT_ASC
            )
          }
          text='サイト閲覧'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(4)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.VIEW_PRODUCT_COUNT_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.VIEW_PRODUCT_COUNT_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc'
                ? CustomerOrderBy.VIEW_PRODUCT_COUNT_DESC
                : CustomerOrderBy.VIEW_PRODUCT_COUNT_ASC
            )
          }
          text='商品閲覧'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(5)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.CART_COUNT_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.CART_COUNT_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.CART_COUNT_DESC : CustomerOrderBy.CART_COUNT_ASC
            )
          }
          text='カート'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(6)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.ORDER_COUNT_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.ORDER_COUNT_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.ORDER_COUNT_DESC : CustomerOrderBy.ORDER_COUNT_ASC
            )
          }
          text='受注数'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(7)}>
        <Text size={12} color={TextColor.SECONDARY}>
          デバイス
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(8)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.FIRST_SEEN_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.FIRST_SEEN_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.FIRST_SEEN_DESC : CustomerOrderBy.FIRST_SEEN_ASC
            )
          }
          text='初回アクセス'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(9)}>
        <ListHeaderSortButton
          order={
            orderBy == CustomerOrderBy.LAST_SEEN_ASC
              ? 'asc'
              : orderBy == CustomerOrderBy.LAST_SEEN_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? CustomerOrderBy.LAST_SEEN_DESC : CustomerOrderBy.LAST_SEEN_ASC
            )
          }
          text='最終アクセス'
        />
      </TableHeadItem>
    </>
  )
}

interface Props {
  customers: CustomerSummary[]
  total: number
  condition: CustomerSearchCondition
  setCondition: Dispatch<SetStateAction<CustomerSearchCondition>>
  onUpdatePage: (page: number) => void
}

const CUSTOMER_COLUMN_LENGTH = 7

const AccessCustomerListTable = ({
  customers,
  total,
  condition,
  setCondition,
  onUpdatePage
}: Props) => {
  const siteId = useSiteId()
  const { showModal } = useContext(AccessCustomerModalContext)

  const listFollowProps = useListScrollFollow(CUSTOMER_COLUMN_LENGTH)
  const { header } = listFollowProps

  const theadProps: TheadProps = {
    ...header,
    orderBy: condition.orderBy,
    onChangeOrderBy: (orderBy: CustomerOrderBy | null) => {
      setCondition((condition) => ({ ...condition, page: 1, orderBy }))
    }
  }

  return (
    <>
      <ScrollFollowTable
        listFollowProps={listFollowProps}
        tableHead={<Thead {...theadProps} />}
        tableFloatHead={<Thead {...theadProps} float />}
        body={
          <>
            {customers.map((customer) => (
              <AccessCustomerListRow
                key={customer.visitor_id}
                customer={customer}
                onAnalysis={() =>
                  showModal({
                    siteId: siteId,
                    visitorId: customer.visitor_id
                  })
                }
              />
            ))}
          </>
        }
        condition={condition}
        onUpdatePage={onUpdatePage}
        total={total}
      />
    </>
  )
}

export default AccessCustomerListTable
